import {
  type ForwardedRef,
  forwardRef,
  memo,
  type MouseEvent,
  type PropsWithChildren,
} from "react";

import { Icon, type IconName } from "@react/components/Icon";
import { Link } from "@react/components/Link";
import { focusRing } from "@react/utilities";
import { type TTailwindString, tw } from "classnames";

import { buttonStyles } from "./styles";

export type ButtonType =
  | "primary"
  | "primary-alt"
  | "secondary"
  | "secondary-white"
  | "secondary-alt"
  | "tertiary"
  | "quaternary"
  | "quaternary-alt"
  | "pill-char"
  | "pill-brick"
  | "pill-outline"
  | "link";
export type Size = "small" | "large" | "pill";

type OptionalProps = Partial<{
  "aria-label": string;
  className: TTailwindString;
  disabled: boolean;
  fluid: boolean;
  iconClassName: TTailwindString;
  iconName: IconName;
  iconPosition: "start" | "end";
  id: string;
  inherit: boolean;
  inline: boolean;
  isLoading?: boolean;
  mtrAs: string;
  mtrTarget: string;
  mtrTrack: "click" | false;
  rounded: boolean;
  size: Size;
  type: ButtonType;
}>;

type ButtonAsLink = OptionalProps & {
  as: "link";
  external?: boolean;
  to: string;
};

type ButtonAsButton = OptionalProps & {
  as?: "button";
  onClick: (e: MouseEvent<HTMLElement>) => void;
};

type props = {
  loading: string;
  width: number | "auto";
};

const ButtonLoadingSpinner = ({ loading, width }: props) => {
  return (
    <div
      className={tw("flex", "items-center", "justify-center")}
      style={{ width }}
    >
      <div className={tw("animate-fade-in")} role="status">
        <svg
          aria-hidden="true"
          className={tw(
            "flex",
            "h-4.5",
            "w-4.5",
            "animate-spin",
            "fill-white/80" as TTailwindString,
            "text-white/20" as TTailwindString
          )}
          fill="none"
          viewBox="0 0 100 101"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span className="sr-only">{loading}</span>
      </div>
    </div>
  );
};

const ButtonChildBase = (
  {
    children,
    fluid,
    hasEndingIcon = false,
    hasStartingIcon = false,
    iconClassName,
    iconName,
    type,
  }: PropsWithChildren<{
    fluid: boolean;
    hasEndingIcon: boolean | undefined;
    hasStartingIcon: boolean | undefined;
    iconClassName: TTailwindString | undefined;
    iconName: IconName | undefined;
    type: ButtonType;
  }>,
  ref: ForwardedRef<HTMLDivElement>
) => (
  <div
    className={tw(
      "flex",
      {
        [tw("flex-1", "justify-center")]: fluid,
      },
      {
        [tw("gap-1.5")]: type.startsWith("pill"),
        [tw("gap-2")]: !type.startsWith("pill"),
      },
      "items-center"
    )}
    ref={ref}
  >
    {hasStartingIcon && iconName && (
      <Icon
        className={iconClassName ?? tw("h-4", "w-4")}
        name={iconName}
      ></Icon>
    )}
    {children}
    {hasEndingIcon && iconName && (
      <Icon
        className={iconClassName ?? tw("h-4", "w-4")}
        name={iconName}
      ></Icon>
    )}
  </div>
);

ButtonChildBase.displayName = "ButtonChild";

const ButtonChild = forwardRef(ButtonChildBase);

const ButtonViewBase = (
  {
    "aria-label": ariaLabel,
    as,
    children,
    className,
    disabled = false,
    fluid = false,
    iconClassName,
    iconName,
    iconPosition,
    id,
    inherit = false,
    inline = false,
    isLoading = false,
    loading = "Loading...",
    mtrAs = "clickModule",
    mtrTarget = "cta",
    mtrTrack = "click",
    rounded = true,
    size = "large",
    type = "primary",
    ...props
  }: PropsWithChildren<ButtonAsLink | ButtonAsButton> & { loading?: string },
  ref: ForwardedRef<HTMLDivElement>
) => {
  const hasEndingIcon = iconName && iconPosition === "end";
  const hasStartingIcon = iconName && !hasEndingIcon;

  if (!ref || !("current" in ref)) {
    return null;
  }

  const classNames = tw(
    buttonStyles({
      disabled,
      fluid,
      hasIcon: iconName !== undefined,
      inherit,
      inline,
      rounded,
      size,
      type,
    }),
    focusRing,
    {
      "no-underline": as === "link",
    },
    {
      [tw(className)]: className !== undefined,
    }
  );

  if (as === "link" && !!("to" in props)) {
    const linkProps = {
      className: classNames,
      external: props.external,
      linkAriaLabel: ariaLabel,
      mtrAs,
      mtrTarget,
      mtrTrack,
      to: props.to,
      unstyled: true,
    };
    return (
      <Link {...linkProps}>
        {isLoading ? (
          <ButtonLoadingSpinner
            loading={loading}
            width={ref?.current?.offsetWidth ?? "auto"}
          />
        ) : (
          <ButtonChild
            fluid={fluid}
            hasEndingIcon={hasEndingIcon}
            hasStartingIcon={hasStartingIcon}
            iconClassName={iconClassName}
            iconName={iconName}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ref={ref as unknown as any}
            type={type}
          >
            {children}
          </ButtonChild>
        )}
      </Link>
    );
  }

  const resolvedProps = {
    className: classNames,
    ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
    ...(disabled && { disabled: true }),
    ...(id ? { id } : {}),
    ...("onClick" in props ? { onClick: props.onClick } : {}),
    ...(mtrTrack
      ? {
          "data-mtr-as": mtrAs,
          "data-mtr-target": mtrTarget,
          "data-mtr-track": mtrTrack,
        }
      : {}),
  };

  return (
    <button {...resolvedProps}>
      {isLoading ? (
        <ButtonLoadingSpinner
          loading={loading}
          width={ref?.current?.offsetWidth ?? 0}
        />
      ) : (
        <ButtonChild
          fluid={fluid}
          hasEndingIcon={hasEndingIcon}
          hasStartingIcon={hasStartingIcon}
          iconClassName={iconClassName}
          iconName={iconName}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ref={ref as unknown as any}
          type={type}
        >
          {children}
        </ButtonChild>
      )}
    </button>
  );
};

export const ButtonView = memo(forwardRef(ButtonViewBase));
